import React, { useState } from 'react';
import { Box, Popover } from '@mui/material';
import { formatCurrency } from '../../../../utils';
import mappedIcon from 'assets/images/mappedicon.svg';

interface Calculation {
  name: string;
  value: number;
  operation?: 'add' | 'subtract';
  displayRole?: 'subtotal';
  isPrimary?: boolean;
  sectionLabel?: string;
  isLineItem?: boolean;
  hideDividerAfter?: boolean;
  differenceLabel?: string;
  positiveDifferenceLabel?: string;
  negativeDifferenceLabel?: string;
  hideOperationPrefix?: boolean;
}

interface CalculatedValueDisplayProps {
  calculations?: Calculation[];
  formatFunction?: (value: number) => string;
  isNA?: boolean;
  style?: React.CSSProperties;
  showZeroDiscrepancyPopover?: boolean;
}

const CalculatedValueDisplay: React.FC<CalculatedValueDisplayProps> = ({
  style,
  calculations,
  formatFunction = formatCurrency,
  isNA = false,
  showZeroDiscrepancyPopover = false
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  // If isNA is true, show "-"
  if (isNA) {
    return <span style={{ color: '#666' }}>-</span>;
  }

  // If no calculations provided, return empty
  if (!calculations || calculations.length === 0) {
    return <span>-</span>;
  }

  // If only one calculation element, show just that value without popover
  if (calculations.length === 1) {
    const singleValue = calculations[0]?.value || 0;
    return <span>{formatFunction(singleValue)}</span>;
  }

  // Calculate the total
  const total = calculations.reduce((sum, item) => {
    if (item.displayRole === 'subtotal') {
      return sum;
    }

    const value = Number(item.value) || 0;
    return item.operation === 'subtract' ? sum - value : sum + value;
  }, 0);

  // Show the primary value when a calculation provides a subtotal, otherwise show the first value.
  const primaryCalculation = calculations.find((item) => item.isPrimary) ?? calculations[0];
  const primaryValue = primaryCalculation?.value || 0;
  const hasNonZeroCalculationValue = calculations.some((item) => Number(item.value) !== 0);
  const shouldShowZeroDiscrepancyState = total === 0 && showZeroDiscrepancyPopover && hasNonZeroCalculationValue;
  const shouldShowPopover = total !== 0 || shouldShowZeroDiscrepancyState;
  const differenceLabel = calculations.find((item) => item.differenceLabel)?.differenceLabel ?? 'Discrepancy';
  const positiveDifferenceLabel = calculations.find((item) => item.positiveDifferenceLabel)?.positiveDifferenceLabel;
  const negativeDifferenceLabel = calculations.find((item) => item.negativeDifferenceLabel)?.negativeDifferenceLabel;

  if (!shouldShowPopover) {
    return <span>{formatFunction(primaryValue)}</span>;
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          cursor: 'pointer',
          ...style
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '2px'
          }}
        >
          {total === 0 && shouldShowZeroDiscrepancyState && (
            <img
              src={mappedIcon}
              alt="matched"
              style={{ width: '16px', height: '16px' }}
            />
          )}
          <span style={{ color: '#23243A' }}>{formatFunction(primaryValue)}</span>
        </div>
        {total !== 0 && (
          <span style={{ color: total < 0 ? 'red' : total > 0 ? 'blue' : '#23243A' }}>
            {`${total > 0 ? '+' : '-'}${formatFunction(Math.abs(total))}`}
          </span>
        )}
      </div>

      <Popover
        open={shouldShowPopover && Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        onClose={handleMouseLeave}
        disableRestoreFocus
        disableScrollLock
        sx={{
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ p: 2, minWidth: '280px', backgroundColor: '#f5f5f5' }}>
          <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>
            {calculations.map((item, index) => {
              const isSubtotal = item.displayRole === 'subtotal';
              const nextIsSubtotal = calculations[index + 1]?.displayRole === 'subtotal';
              const isLineItem = !isSubtotal && (index !== 0 || item.isLineItem);
              const operationPrefix = index === 0 || isSubtotal || item.hideOperationPrefix ? '' : (item.operation === 'subtract' ? '-' : '+');

              return (
                <React.Fragment key={index}>
                  {item.sectionLabel && (
                    <div style={{
                      color: '#555',
                      fontSize: '0.75em',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      marginTop: index === 0 ? 0 : '10px',
                      marginBottom: '4px',
                      textTransform: 'uppercase'
                    }}>
                      {item.sectionLabel}
                    </div>
                  )}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '16px',
                    marginBottom: index < calculations.length - 1 ? '4px' : '8px',
                    borderTop: isSubtotal ? '1px solid #999' : 'none',
                    borderBottom: index < calculations.length - 1 && !isSubtotal && !nextIsSubtotal && !item.hideDividerAfter ? '1px solid #ddd' : 'none',
                    paddingTop: isSubtotal ? '6px' : '0',
                    paddingBottom: index < calculations.length - 1 ? '4px' : '0',
                    fontWeight: isSubtotal ? 700 : undefined,
                    fontSize: isSubtotal ? '0.95em' : undefined
                  }}>
                    <span style={{ color: isSubtotal ? '#23243A' : '#666' }}>
                      {!isLineItem ? (
                        item.name
                      ) : (
                        <span style={{ fontSize: '0.85em' }}>
                          {item.hideOperationPrefix ? '' : `${item.operation === 'subtract' ? 'less' : 'add'} `}{item.name}
                        </span>
                      )}
                    </span>
                    <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      {operationPrefix} {formatFunction(item.value)}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '16px',
              borderTop: '2px solid #333',
              paddingTop: '8px',
              marginTop: '8px',
              fontWeight: 'bold',
              fontSize: '0.92em'
            }}>
              <span>{differenceLabel}</span>
              {total === 0 ? (
                <img src={mappedIcon} alt="matched" style={{ width: '18px', height: '18px' }} />
              ) : (
                <span style={{ color: total >= 0 ? '#0000FF' : 'red', whiteSpace: 'nowrap' }}>
                  {total >= 0 ? '+' : '-'}{formatFunction(Math.abs(total))}
                </span>
              )}
            </div>
            {total !== 0 && (
              <div style={{
                fontSize: '0.75em',
                color: '#666',
                marginTop: '4px'
              }}>
                {positiveDifferenceLabel && negativeDifferenceLabel ? (
                  <span style={{ color: total >= 0 ? '#0000FF' : 'red' }}>
                    {total >= 0 ? positiveDifferenceLabel : negativeDifferenceLabel} {total >= 0 ? '+' : '-'}{formatFunction(Math.abs(total))}
                  </span>
                ) : total >= 0 ?
                  <span style={{ color: '#0000FF' }}>
                    +ve is profit
                  </span>
                  :
                  <span style={{ color: 'red' }}>
                    -ve is loss
                  </span>
                }
              </div>
            )}
          </div>
        </Box>
      </Popover>
    </>
  );
};

export default CalculatedValueDisplay;
